import { useCallback } from 'react';
import { useSSEStream } from '@teetsh/app/src/hooks/useSSEStream';

export interface AIStreamOptions {
  onComplete?: () => void;
}

export interface AIStreamResult {
  data: string;
  error: string | null;
  isLoading: boolean;
  startGeneration: (params: {
    text: string;
    improvementType: string;
    userPrompt?: string;
    [key: string]: any; // Allow additional context params
  }) => void;
  stop: () => void;
  reset: () => void;
}

export function useAIStream(
  endpoint: string,
  options: AIStreamOptions = {},
): AIStreamResult {
  const { onComplete } = options;

  const { data, error, isLoading, startStream, stopStream, reset } =
    useSSEStream(endpoint, {
      onComplete,
    });

  const startGeneration = useCallback(
    (params: {
      text: string;
      improvementType: string;
      userPrompt?: string;
      [key: string]: any;
    }) => {
      // Don't spread params directly - preserve structure for fields like 'context'
      const body: Record<string, any> = {
        text: params.text,
        improvementType: params.improvementType,
        stream: true,
      };

      // Add optional fields if present
      if (params.userPrompt) {
        body.userPrompt = params.userPrompt;
      }

      // Pass through any other fields (like context) as-is
      Object.keys(params).forEach(key => {
        if (!['text', 'improvementType', 'userPrompt'].includes(key)) {
          body[key] = params[key];
        }
      });

      startStream(body);
    },
    [startStream],
  );

  return {
    data,
    error,
    isLoading,
    startGeneration,
    stop: stopStream,
    reset,
  };
}
